import { useState } from 'react'
import { useUsers, useUpdateUserRole, useUserDependencies, useDeleteUser } from '@/hooks/use-users'
import { useAuth } from '@/hooks/use-auth'
import { Modal } from '@/components/modal'
import type { User, UserRole } from '@/types'

const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Admin',
  viewer: 'Viewer',
  new_user: 'New User',
}

const ROLE_COLORS: Record<UserRole, string> = {
  admin: 'bg-brand-teal/10 text-brand-teal',
  viewer: 'bg-blue-100 text-blue-700',
  new_user: 'bg-gray-100 text-gray-600',
}

function DeleteUserModal({ user, onClose }: { user: User; onClose: () => void }) {
  const [confirmText, setConfirmText] = useState('')
  const { data: deps, isLoading } = useUserDependencies(user.id)
  const deleteUser = useDeleteUser()

  const hasDeps = deps?.has_dependencies ?? false
  const nameMatch = confirmText === user.name

  const handleDelete = async () => {
    try {
      await deleteUser.mutateAsync(user.id)
      onClose()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed')
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="Delete User" size="sm">
      {isLoading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-teal" />
        </div>
      ) : hasDeps ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            This user still owns resources that must be reassigned before deletion:
          </p>
          <ul className="text-sm text-gray-600 space-y-1">
            {(deps?.goals ?? 0) > 0 && <li><strong>{deps!.goals}</strong> goal{deps!.goals !== 1 ? 's' : ''}</li>}
            {(deps?.action_plans ?? 0) > 0 && <li><strong>{deps!.action_plans}</strong> action plan{deps!.action_plans !== 1 ? 's' : ''}</li>}
            {(deps?.bonuses ?? 0) > 0 && <li><strong>{deps!.bonuses}</strong> bonus config{deps!.bonuses !== 1 ? 's' : ''}</li>}
          </ul>
          <p className="text-sm text-gray-500">
            Reassign all goals, action plans, and bonuses to another user first.
          </p>
          <div className="flex justify-end">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
              Close
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            This action is permanent and cannot be undone. Type <strong>{user.name}</strong> to confirm.
          </p>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={user.name}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={!nameMatch || deleteUser.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {deleteUser.isPending ? 'Deleting...' : 'Delete User'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}

export function AdminUsers() {
  const { user: currentUser } = useAuth()
  const { data: users, isLoading } = useUsers()
  const updateRole = useUpdateUserRole()
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null)

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    await updateRole.mutateAsync({ userId, role: newRole })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Manage Users</h1>
        <p className="text-sm text-gray-500 mt-1">Control user access and permissions</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">User</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Role</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Joined</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {users?.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-600">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{user.name}</p>
                      {user.id === currentUser?.id && (
                        <span className="text-xs text-gray-500">(You)</span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">{user.email || '—'}</td>
                <td className="px-6 py-4">
                  {user.id === currentUser?.id ? (
                    // Can't change own role
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${ROLE_COLORS[user.role]}`}>
                      {ROLE_LABELS[user.role]}
                    </span>
                  ) : (
                    <select
                      value={user.role}
                      onChange={(e) => handleRoleChange(user.id, e.target.value as UserRole)}
                      disabled={updateRole.isPending}
                      className="rounded-lg border border-gray-300 px-2 py-1 text-sm focus:ring-2 focus:ring-brand-teal focus:border-transparent"
                    >
                      <option value="admin">Admin</option>
                      <option value="viewer">Viewer</option>
                      <option value="new_user">New User</option>
                    </select>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {new Date(user.created_at * 1000).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 text-right">
                  {user.id !== currentUser?.id && (
                    <button
                      onClick={() => setDeleteTarget(user)}
                      title="Delete user"
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {(!users || users.length === 0) && (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-2">Role Permissions</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li><strong>Admin:</strong> Full access - manage metrics, goals, users, and view all data</li>
          <li><strong>Viewer:</strong> View all metrics and goals, see own bonus amounts only</li>
          <li><strong>New User:</strong> No access until assigned a role by an admin</li>
        </ul>
      </div>

      {deleteTarget && (
        <DeleteUserModal user={deleteTarget} onClose={() => setDeleteTarget(null)} />
      )}
    </div>
  )
}
